import type { AxiosInstance } from 'axios';
import { Result } from 'services/Result';
import { injectable } from 'inversify';
import { ISessionVerificationProvider } from 'interfaces/providers/ISessionVerificationProvider';
import { ISessionWithPlaysUpdateRequest } from 'interfaces/session/ISessionWithPlaysUpdateRequest';
import { notify } from 'services/NotificationService';
import { IPlayMetadata, IBallFinal, IPlayer } from '@trackman/baseball-shared-components';
import { IBoxScoreRequest } from 'interfaces/boxScore/IBoxScoreRequest';
import { IBoxScoreResponse, IBoxScoreResponseWrapper } from 'interfaces/boxScore/IBoxScoreResponse';
import { LogService } from 'services/LogService';
import { ILeague, ILevel, ISession, IField, IVenue } from '../../interfaces/session/ISession';
import { ISearchTeam } from '../../interfaces/team/ISearchTeam';
import { ISchemaValidationErrorResponse } from '../../interfaces/sessionVerification/ISchemaValidationErrorResponse';
import { SchemaValidationError } from '../SchemaValidationError';
import { ISchemaValidationSuccessResponse } from '../../interfaces/sessionVerification/ISchemaValidationSuccessResponse';
import { IOwningOrganization } from '../../interfaces/session/IOwningOrganization';
import { IUmpire } from 'interfaces/umpire/IUmpire';
import { ISessionLockRequest, ISessionLockResponse } from 'interfaces/session/ISessionLock';

@injectable()
export class SessionVerificationApiService implements ISessionVerificationProvider {
  private axiosInstance: AxiosInstance;

  private logService: LogService;

  private shouldSendLockRequest = true;

  constructor(axiosInstance: AxiosInstance, logService: LogService) {
    this.axiosInstance = axiosInstance;
    this.logService = logService;
  }

  getSessions = async (date: string, teamId?: string) => {
    try {
      const response = await this.axiosInstance.get<Result<any>>(
        `api/session?date=${date}${teamId === 'undefined' ? '' : `&id=${teamId}`}`
      );
      if (response.data && !response.data.isError && response.data.data) {
        if (response.data.data.length === 0) {
          notify('No sessions', `No sessions for date: ${date} and team id: ${teamId}`, 'info');
        }
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting session. teamId : ${teamId} , date : ${date}` });
      throw Error('Failed to fetch sessions');
    }
  };

  getSuggestedSessions = async (sessionId: string, timeFrameDays: number) => {
    try {
      const response = await this.axiosInstance.get<Result<ISession[]>>(
        `api/session/${sessionId}/suggestedSessions?timeFrameDays=${timeFrameDays}`
      );
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({
        error: err,
        message: `Exception when getting session. sessionId : ${sessionId} , timeFrameDays : ${timeFrameDays}`,
      });
      throw Error('Failed to fetch sessions');
    }
  };

  getSessionById = async (sessionId: string) => {
    try {
      const response = await this.axiosInstance.get<Result<ISession>>(`api/session/${sessionId}`);
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting session. sessionId : ${sessionId}` });
      throw Error(`Failed to fetch session with id: ${sessionId}`);
    }
  };

  getPlaysForSession = async (sessionId: string) => {
    try {
      const response = await this.axiosInstance.get<Result<IPlayMetadata[]>>(`api/session/${sessionId}/plays`);
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting plays for session. sessionId : ${sessionId}` });
      throw Error(`Failed to fetch plays for session with id: ${sessionId}`);
    }
  };

  getBallFinalsForSession = async (sessionId: string) => {
    try {
      const response = await this.axiosInstance.get<Result<IBallFinal[]>>(`api/session/${sessionId}/ballFinals`);
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting ball finals for session. sessionId : ${sessionId}` });
      throw Error(`Failed to fetch ballFinals for session with id: ${sessionId}`);
    }
  };

  updateSession = async (sessionUpdate: ISessionWithPlaysUpdateRequest) => {
    try {
      const response = await this.axiosInstance.post<any>(`api/session`, sessionUpdate);

      if (response.status === 200) {
        return response.data.data as ISchemaValidationSuccessResponse;
      }
      if (response.status === 400) {
        const errors = response.data as ISchemaValidationErrorResponse;
        throw new SchemaValidationError(errors);
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      if (err instanceof SchemaValidationError) {
        this.logService.logError({ error: err, message: `Schema validation exception when submitting  session.` });
        throw Error('There were validation errors when submitting the data. Please hit F12 and see console output for validation log.');
      }

      this.logService.logError({ error: err, message: `Exception when submitting  session.` });

      throw Error('Failed to update session');
    }
  };

  updateSessionSettings = async (sessionUpdate: ISessionWithPlaysUpdateRequest) => {
    try {
      const response = await this.axiosInstance.post<any>(`api/session`, sessionUpdate);

      if (response.status === 200) {
        return response.data.data as ISchemaValidationSuccessResponse;
      }
      if (response.status === 400) {
        const errors = response.data as ISchemaValidationErrorResponse;
        throw new SchemaValidationError(errors);
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      if (err instanceof SchemaValidationError) {
        this.logService.logError({ error: err, message: `Schema validation exception when submitting  session.` });
        throw Error('There were validation errors when submitting the data. Please hit F12 and see console output for validation log.');
      }

      this.logService.logError({ error: err, message: `Exception when submitting  session.` });

      throw Error('Failed to update session');
    }
  };

  getTeams = async () => {
    try {
      const response = await this.axiosInstance.get<Result<ISearchTeam[]>>('api/contextualData/team');
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting teams.` });
      throw Error('Failed to fetch teams');
    }
  };

  getTeamsByName = async (keyword: string) => {
    try {
      const response = await this.axiosInstance.get<Result<ISearchTeam[]>>(`api/contextualData/team/search?teamNameContains=${keyword}`);
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting teams.` });
      throw Error('Failed to fetch teams');
    }
  };

  getLeagues = async () => {
    try {
      const response = await this.axiosInstance.get<Result<ILeague[]>>('api/contextualData/league');
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting leagues.` });
      throw Error('Failed to fetch leagues');
    }
  };

  getLevels = async () => {
    try {
      const response = await this.axiosInstance.get<Result<ILevel[]>>('api/contextualData/level');
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting levels.` });
      throw Error('Failed to fetch levels');
    }
  };

  getFields = async () => {
    try {
      const response = await this.axiosInstance.get<Result<IField[]>>('api/contextualData/field');
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting fields.` });
      throw Error('Failed to fetch fields');
    }
  };

  getVenues = async () => {
    try {
      const response = await this.axiosInstance.get<Result<IVenue[]>>('api/contextualData/venue');
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting venues.` });
      throw Error('Failed to fetch venues');
    }
  };

  getOwningOrganizations = async (fieldId: string) => {
    try {
      const response = await this.axiosInstance.get<Result<IOwningOrganization[]>>(
        `api/contextualData/owningOrganization/field/${fieldId}`
      );
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting owning organizations.` });
      throw Error('Failed to fetch owningOrganizations');
    }
  };

  getRoster = async (teamId: string) => {
    try {
      const response = await this.axiosInstance.get<Result<IPlayer[]>>(`api/contextualData/roster/${teamId}`);
      if (response.data && !response.data.isError && response.data.data) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Exception when getting rosters.` });
      throw Error(`Failed to fetch roster for team: ${teamId}`);
    }
  };

  mergeSessions = async (masterSessionId: string, slaveSessionId: string) => {
    try {
      const response = await this.axiosInstance.post<Result<void>>('api/session/mergesessions', {
        MasterSessionId: masterSessionId,
        SlaveSessionId: slaveSessionId,
      });
      if (response.status === 200) {
        return;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      this.logService.logError({ error: err, message: `Eception when merging.` });
      throw Error('Failed to initiate merging process');
    }
  };

  getSecondSourceBoxScore = async (sessionId: string): Promise<IBoxScoreResponse> => {
    try {
      const response = await this.axiosInstance.get<Result<IBoxScoreResponseWrapper>>(`api/boxscore/secondSource/${sessionId}`);
      if (response.data && !response.data.isError && response.data.data?.boxScore) {
        return response.data.data.boxScore;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      throw Error(`Failed to fetch SecondSource box score for session: ${sessionId}`);
    }
  };

  getTrackmanBoxScore = async (request: IBoxScoreRequest): Promise<IBoxScoreResponse> => {
    try {
      const response = await this.axiosInstance.post<Result<IBoxScoreResponseWrapper>>('api/boxscore/trackman/', request);
      if (response.data && !response.data.isError && response.data.data?.boxScore && response.data.data.status === 'OK') {
        return response.data.data.boxScore;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      throw Error('Failed to fetch Trackman box score');
    }
  };

  validateExternalSessionId = async (externalSessionId: string): Promise<boolean | undefined> => {
    try {
      const response = await this.axiosInstance.get<Result<boolean>>(`api/contextualData/scheduledEvent/${externalSessionId}/exists`);
      if (response.data && !response.data.isError) {
        return response.data.data;
      }
      throw Error(JSON.stringify(response));
    } catch (err) {
      throw Error('Failed to fetch Trackman box score');
    }
  };

  GetUmpires = async (): Promise<IUmpire[]> => {
    const response = await this.axiosInstance.get<Result<IUmpire[]>>('api/contextualData/umpire');
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error(`Failed to fetch umpires: ${response.status}`);
  };
}
